import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedSection } from '../components/AnimatedSection';
import { Construction, Play, ArrowUpRight } from 'lucide-react';

/* ─── 2D Animation Projects ─────────────────────────────────────── */
const animationProjects = [
  { title: 'WayWheel', client: 'WayWheel', desc: 'Engaging 2D animation video that simplifies brand messaging through storytelling and motion.' },
  { title: 'SGN Marine', client: 'Guru', desc: 'Animation video that clearly communicates marine services with smooth visual storytelling.' },
];

/* ─── Packaging Projects ────────────────────────────────────────── */
const packagingProjects = [
  {
    id: 'planet-soul',
    title: 'Planet Soul',
    subtitle: 'Compostable Garbage Bags',
    tag: 'Eco Packaging',
    year: '2024',
    desc: 'A nature-forward packaging system for compostable garbage bags — designed to communicate sustainability at a glance with character-driven illustration and earthy craft materials.',
    insight: 'Emotionally intelligent design that turns a commodity into a cause.',
    img: 'https://customer-assets.emergentagent.com/job_motion-design-10/artifacts/zce9xp77_Image%207.png',
    interactive: true,
    variants: [
      { name: 'Fresh Forest',   color: '#2D7A4A', light: '#D5EED9', label: 'COMPOSTABLE · BIO-BASED' },
      { name: 'Ocean Blue',     color: '#1565C0', light: '#D0E4F8', label: 'PLASTIC-FREE · CERTIFIED' },
      { name: 'Earth Brown',    color: '#7B4F1A', light: '#F5E6D0', label: 'KRAFT · RECYCLED FIBRE' },
      { name: 'Leaf Green',     color: '#558B2F', light: '#DCEDC8', label: 'ZERO WASTE · ECO-SEAL' },
    ],
  },
  {
    id: 'sobisco',
    title: 'Sobisco Swiss Roll',
    subtitle: 'Chocolate & Butterscotch Variants',
    tag: 'Food Packaging',
    year: '2024',
    desc: 'Two-variant packaging redesign for Sobisco\'s Swiss Roll — leveraging rich product photography and flavour-coded colour systems to drive impulse purchase at retail shelf.',
    insight: 'Visual taste appeal: packaging that makes you hungry before you even open it.',
    img: 'https://customer-assets.emergentagent.com/job_motion-design-10/artifacts/rxwvdskl_Image%208.png',
    interactive: true,
    variants: [
      { name: 'Chocolate',      color: '#4E2010', light: '#F5E0D0', label: 'RICH DARK COCOA' },
      { name: 'Butterscotch',   color: '#C17B2F', light: '#FDE8C8', label: 'GOLDEN BUTTER CREAM' },
      { name: 'Vanilla',        color: '#C49A14', light: '#FBF5C8', label: 'CLASSIC CREAM' },
      { name: 'Caramel',        color: '#A0522D', light: '#FAE5D0', label: 'SMOOTH CARAMEL' },
    ],
  },
  {
    id: 'linc-markline',
    title: 'Linc Markline',
    subtitle: "Children's Stationery Range",
    tag: 'Stationery Packaging',
    year: '2023',
    desc: 'A full packaging suite for Linc\'s children\'s stationery range — playful illustration, vibrant color systems, and packaging that sparks creative excitement in young minds.',
    insight: 'Context-aware design: packaging that lives on desks, in bags, in classrooms.',
    img: 'https://customer-assets.emergentagent.com/job_motion-design-10/artifacts/h5s8o919_Image%209.png',
    interactive: true,
    variants: [
      { name: 'Ocean Teal',     color: '#1A8FA0', light: '#D4F0F5', label: 'GEL PENS & MARKERS' },
      { name: 'Vivid Purple',   color: '#7B3FA0', light: '#EDD8F5', label: 'SKETCH & COLOUR' },
      { name: 'Sunrise Orange', color: '#D4620A', light: '#FDE8D0', label: 'HIGHLIGHTERS' },
      { name: 'Grass Green',    color: '#2D8A3A', light: '#D5EDD8', label: 'CRAFT & ART' },
      { name: 'Petal Pink',     color: '#C43A6A', light: '#FAD8E8', label: 'STICKER & NOTES' },
    ],
  },
  {
    id: 'iffco',
    title: 'IFFCO Fertilizers',
    subtitle: 'Samriddhi · Pragati · Unnati',
    tag: 'Agricultural Packaging',
    year: '2023',
    desc: 'Three distinct packaging identities for IFFCO\'s fertilizer sub-brands — each with a strong color system, clear technical communication, and modern agricultural imagery.',
    insight: 'Clarity for the farmer: complex chemistry made legible and trustworthy.',
    img: 'https://customer-assets.emergentagent.com/job_motion-design-10/artifacts/wue2aaqu_image%2010.png',
    interactive: true,
    variants: [
      { name: 'Samriddhi',      color: '#2E7D32', light: '#D8F0D8', label: 'CROP NUTRITION' },
      { name: 'Pragati',        color: '#1565C0', light: '#D0E4F8', label: 'SOIL HEALTH' },
      { name: 'Unnati',         color: '#E65100', light: '#FFE0CC', label: 'YIELD BOOST' },
      { name: 'Harvest Gold',   color: '#C49A14', light: '#FBF0C8', label: 'ROOT GROWTH' },
    ],
  },
  {
    id: 'goodricke',
    title: 'Goodricke Roasted',
    subtitle: 'Darjeeling Tea — Poila Baishakh Edition',
    tag: 'Premium Tea Packaging',
    year: '2024',
    desc: 'A limited-edition packaging design for Goodricke\'s Roasted Darjeeling Tea — celebrating the Bengali New Year with intricate folk-art illustration and a heritage colour palette.',
    insight: 'Lived-in cultural authenticity: packaging that tells a story before you taste it.',
    img: 'https://customer-assets.emergentagent.com/job_motion-design-10/artifacts/45r8kbjh_image%2011.png',
    interactive: true,
    variants: [
      { name: 'Heritage Red',   color: '#8B1A1A', light: '#FAD8D8', label: 'ROASTED DARJEELING' },
      { name: 'Poila Gold',     color: '#B8860B', light: '#FBF0D0', label: 'FIRST FLUSH' },
      { name: 'Midnight',       color: '#1A2A4A', light: '#D0D8F0', label: 'SECOND FLUSH' },
      { name: 'Forest Brown',   color: '#5D3A1A', light: '#F0E0D0', label: 'AUTUMNAL BLEND' },
    ],
  },
  {
    id: 'arochem',
    title: 'Arochem Car Perfumes',
    subtitle: 'Travel-Inspired Fragrance Collection',
    tag: 'Lifestyle Packaging',
    year: '2024',
    desc: 'A travel-themed fragrance collection for Arochem — each variant (Rainy Hills, Romantic Drive, Happy Journey, Rain Forest, Miami Blue) with its own visual identity and destination-inspired illustration.',
    insight: "Selling the feeling: packaging that transports you before you've even smelled it.",
    img: 'https://customer-assets.emergentagent.com/job_motion-design-10/artifacts/rljg72qx_image%2012.png',
    interactive: true,
    variants: [
      { name: 'Rainy Hills',    color: '#1B5B8A', light: '#D6E8F5', label: 'FRESH & HERBAL' },
      { name: 'Romantic Drive', color: '#C94B6A', light: '#FAE0E6', label: 'FLORAL & ROSE' },
      { name: 'Happy Journey',  color: '#E07B3A', light: '#FDEBD8', label: 'CITRUS & WARM' },
      { name: 'Rain Forest',    color: '#2D7A4A', light: '#D5EEE0', label: 'WOODY & GREEN' },
      { name: 'Miami Blue',     color: '#1AA6B7', light: '#D4F0F4', label: 'MARINE & CRISP' },
    ],
  },
];

/* ─── WIP categories ────────────────────────────────────────────── */
const wipCategories = [
  { title: 'UI/UX Design',           desc: 'Digital product designs, dashboards, and user experience projects.' },
  { title: 'Branding & Identity',    desc: 'Logo systems, visual identity, and brand strategy work.' },
  { title: 'Instagram Content',      desc: 'Reels, carousels, and social media campaigns.' },
  { title: 'Website Design',         desc: 'Landing pages and digital experience projects.' },
];

/* ─── Video Card ────────────────────────────────────────────────── */
const VideoCard = ({ src, title, desc, index, accentColor = '#3B82F6' }) => {
  const [playing, setPlaying] = useState(false);
  const vidRef = useRef(null);

  const toggle = () => {
    if (!vidRef.current) return;
    if (playing) { vidRef.current.pause(); setPlaying(false); }
    else { vidRef.current.play(); setPlaying(true); }
  };

  return (
    <AnimatedSection delay={index * 0.12}>
      <motion.div 
        className="card-border" 
        style={{ overflow: 'hidden', borderRadius: 14 }} 
        data-testid={`video-card-${index}`}
        whileHover={{ y: -6, boxShadow: `0 20px 40px ${accentColor}20` }}
        transition={{ duration: 0.3 }}
      >
        {/* Video */}
        <div style={{ position: 'relative', background: '#0A0A0A', aspectRatio: '16/9', overflow: 'hidden' }}>
          <video
            ref={vidRef}
            src={src}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            onEnded={() => setPlaying(false)}
            playsInline
          />
          {!playing && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'linear-gradient(135deg, rgba(0,0,0,0.5), rgba(0,0,0,0.7))' }}
              onClick={toggle}
              data-testid={`video-play-${index}`}
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                animate={{ boxShadow: ['0 0 0 0 rgba(255,255,255,0.4)', '0 0 0 20px rgba(255,255,255,0)', '0 0 0 0 rgba(255,255,255,0.4)'] }}
                transition={{ duration: 2, repeat: Infinity }}
                style={{ width: 72, height: 72, borderRadius: '50%', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Play size={26} style={{ color: '#0A0A0A', marginLeft: 4 }} />
              </motion.div>
            </motion.div>
          )}
          {playing && (
            <div
              style={{ position: 'absolute', inset: 0, cursor: 'pointer' }}
              onClick={toggle}
            />
          )}
          {/* Gradient accent */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${accentColor}, ${accentColor}88)` }} />
        </div>
        {/* Info */}
        <div style={{ padding: '20px 24px 24px', borderTop: '1px solid var(--border)', background: 'var(--canvas)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ 
              padding: '4px 12px', 
              borderRadius: 20, 
              background: `${accentColor}15`, 
              color: accentColor, 
              fontFamily: 'JetBrains Mono', 
              fontSize: 10, 
              letterSpacing: '0.06em',
              fontWeight: 500
            }}>
              2D ANIMATION
            </span>
          </div>
          <h3 className="heading-md" style={{ fontSize: 18, marginBottom: 8 }}>{title}</h3>
          <p className="body-base" style={{ fontSize: 13, lineHeight: 1.7 }}>{desc}</p>
        </div>
      </motion.div>
    </AnimatedSection>
  );
};

/* ─── Packaging Card ────────────────────────────────────────────── */
const PackagingCard = ({ project, index }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    let i = 0;
    timerRef.current = setInterval(() => {
      i = (i + 1) % project.variants.length;
      setActiveIdx(i);
    }, 2200);
    return () => clearInterval(timerRef.current);
  }, [project]);

  const pickVariant = (i) => {
    clearInterval(timerRef.current);
    setActiveIdx(i);
  };

  const active = project.variants[activeIdx];

  return (
    <AnimatedSection delay={index * 0.08}>
      <motion.div
        className="card-border"
        style={{ overflow: 'hidden', borderRadius: 10, display: 'flex', flexDirection: 'column' }}
        data-testid={`packaging-card-${index}`}
        whileHover={{ y: -4 }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      >
        {/* Image area */}
        <motion.div
          animate={{ background: active.light }}
          transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          style={{
            position: 'relative',
            height: 360,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            overflow: 'hidden',
          }}
        >
          <motion.img
            src={project.img}
            alt={project.title}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              display: 'block',
              borderRadius: 4,
              filter: 'drop-shadow(0 8px 28px rgba(0,0,0,0.13))',
            }}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          />

          {/* Tag badge */}
          <div style={{ position: 'absolute', top: 14, left: 14, background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(8px)', padding: '3px 10px', borderRadius: 4 }}>
            <span className="font-mono" style={{ fontSize: 10, color: 'var(--ink-700)', letterSpacing: '0.06em' }}>{project.tag}</span>
          </div>
          {/* Year badge */}
          <div style={{ position: 'absolute', top: 14, right: 14, background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(8px)', padding: '3px 10px', borderRadius: 4 }}>
            <span className="font-mono" style={{ fontSize: 10, color: 'var(--ink-500)' }}>{project.year}</span>
          </div>

          {/* Variant dot selectors */}
          <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 8 }}>
              {project.variants.map((v, i) => (
                <motion.button
                  key={i}
                  onClick={() => pickVariant(i)}
                  title={v.name}
                  data-testid={`variant-dot-${i}`}
                  whileHover={{ scale: 1.3 }}
                  whileTap={{ scale: 0.85 }}
                  animate={{ width: activeIdx === i ? 28 : 14 }}
                  style={{
                    height: 14,
                    borderRadius: 999,
                    background: v.color,
                    border: activeIdx === i ? '2px solid white' : '2px solid rgba(255,255,255,0.4)',
                    cursor: 'pointer',
                    padding: 0,
                    boxShadow: activeIdx === i ? `0 0 0 2px ${v.color}55` : 'none',
                    transition: 'box-shadow 0.3s ease',
                  }}
                />
              ))}
            </div>
        </motion.div>

        {/* Active variant name bar — always shown */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIdx}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.3 }}
            style={{
              padding: '8px 20px',
              background: active.light,
              borderBottom: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: active.color, flexShrink: 0 }} />
            <span className="font-mono" style={{ fontSize: 10, color: active.color, letterSpacing: '0.08em' }}>
              {active.name.toUpperCase()}
            </span>
            <span className="font-mono" style={{ fontSize: 10, color: 'var(--ink-400)', letterSpacing: '0.06em', marginLeft: 4 }}>
              · {active.label}
            </span>
          </motion.div>
        </AnimatePresence>

        {/* Info */}
        <div style={{ padding: '20px 24px 28px', background: 'var(--canvas)', flex: 1 }}>
          <h3 className="heading-md" style={{ fontSize: 19, marginBottom: 3 }}>{project.title}</h3>
          <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--accent)', marginBottom: 12 }}>{project.subtitle}</p>
          <p className="body-base" style={{ fontSize: 13, lineHeight: 1.65, marginBottom: 14 }}>{project.desc}</p>
          <div style={{ padding: '10px 14px', background: 'var(--surface)', borderRadius: 6, borderLeft: '2px solid var(--ink-300)' }}>
            <p className="font-mono" style={{ fontSize: 10, color: 'var(--ink-500)', letterSpacing: '0.06em', marginBottom: 4 }}>KEY INSIGHT</p>
            <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--ink-700)', fontStyle: 'italic', lineHeight: 1.5 }}>
              {project.insight}
            </p>
          </div>
        </div>
      </motion.div>
    </AnimatedSection>
  );
};

/* ─── WIP Card ──────────────────────────────────────────────────── */
const WIPCard = ({ title, desc, index }) => (
  <AnimatedSection delay={index * 0.08}>
    <div
      data-testid={`wip-card-${index}`}
      style={{
        border: '1px dashed var(--border)',
        borderRadius: 10,
        padding: '40px 32px',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minHeight: 200,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Construction size={14} style={{ color: 'var(--ink-300)' }} />
        <span className="font-mono" style={{ fontSize: 10, color: 'var(--ink-300)', letterSpacing: '0.1em' }}>WORK IN PROGRESS</span>
      </div>
      <h3 className="heading-md" style={{ fontSize: 20, color: 'var(--ink-700)' }}>{title}</h3>
      <p className="body-base" style={{ fontSize: 13 }}>{desc}</p>
      <p style={{ fontFamily: 'Inter', fontSize: 12, color: 'var(--accent)', marginTop: 4 }}>
        Showcase coming soon.
      </p>
    </div>
  </AnimatedSection>
);

/* ─── Section header ────────────────────────────────────────────── */
const SectionHeader = ({ label, title, desc }) => (
  <AnimatedSection style={{ marginBottom: 48, paddingBottom: 32, borderBottom: '1px solid var(--border)' }}>
    <span className="label-sm">{label}</span>
    <h2 className="heading-lg" style={{ fontSize: 'clamp(28px, 4vw, 48px)', marginTop: 12, marginBottom: desc ? 16 : 0 }}>{title}</h2>
    {desc && <p className="body-base" style={{ maxWidth: 600 }}>{desc}</p>}
  </AnimatedSection>
);

/* ─── Page ──────────────────────────────────────────────────────── */
export default function CaseStudies() {
  const [activeTab, setActiveTab] = useState('packaging');

  const tabs = [
    { id: 'packaging', label: 'Packaging Design' },
    { id: 'animation', label: '2D Animation' },
    { id: 'wip',       label: 'Coming Soon' },
  ];

  return (
    <div data-testid="case-studies-page" style={{ paddingTop: 64, background: 'var(--canvas)' }}>
      {/* Hero */}
      <section style={{ borderBottom: '1px solid var(--border)', paddingTop: 80, paddingBottom: 64 }}>
        <div className="container-site">
          <AnimatedSection>
            <span className="label-sm">Our Work</span>
            <h1 className="heading-xl" style={{ fontSize: 'clamp(48px, 9vw, 100px)', marginTop: 20, marginBottom: 20 }}>
              Project<br /><span style={{ color: 'var(--accent)' }}>Showcase</span>
            </h1>
            <p className="body-base" style={{ maxWidth: 540, fontSize: 16 }}>
              Work that proves clarity is a competitive advantage. More work is on the way.
            </p>
          </AnimatedSection>

          {/* Tab nav */}
          <div style={{ display: 'flex', gap: 2, marginTop: 48, background: 'var(--surface)', padding: 4, borderRadius: 8, width: 'fit-content', border: '1px solid var(--border)' }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                style={{
                  padding: '8px 20px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'Inter',
                  fontSize: 13,
                  fontWeight: 500,
                  background: activeTab === tab.id ? 'var(--canvas)' : 'transparent',
                  color: activeTab === tab.id ? 'var(--ink-950)' : 'var(--ink-500)',
                  boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section style={{ paddingTop: 64, paddingBottom: 80 }}>
        <div className="container-site">
          <AnimatePresence mode="wait">

            {/* PACKAGING */}
            {activeTab === 'packaging' && (
              <motion.div key="packaging" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}>
                <SectionHeader
                  label="Packaging Design"
                  title="Packaging as Experience"
                  desc="Great packaging doesn't just contain a product — it contains a promise. Each project below is a strategic system, not just a visual."
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20 }}>
                  {packagingProjects.map((p, i) => <PackagingCard key={p.id} project={p} index={i} />)}
                </div>
              </motion.div>
            )}

            {/* ANIMATION */}
            {activeTab === 'animation' && (
              <motion.div key="animation" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}>
                <SectionHeader
                  label="2D Animation"
                  title="Motion that communicates."
                  desc="Promotional 2D animation videos created for brand campaigns. Watch below."
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 24 }}>
                  <VideoCard
                    src="https://customer-assets.emergentagent.com/job_motion-design-10/artifacts/xjpf70f9_WhatsApp%20Video%202026-02-25%20at%202.18.03%20PM.mp4"
                    title="Brand Promo Video — 01"
                    desc="A 2D animated promotional video crafted for brand storytelling and audience engagement across digital platforms."
                    index={0}
                  />
                  <VideoCard
                    src="https://customer-assets.emergentagent.com/job_motion-design-10/artifacts/157kd3fj_WhatsApp%20Video%202026-02-25%20at%202.17.33%20PM.mp4"
                    title="Brand Promo Video — 02"
                    desc="A second 2D animated video demonstrating our motion design capabilities for product and brand promotion."
                    index={1}
                  />
                </div>
              </motion.div>
            )}

            {/* WIP */}
            {activeTab === 'wip' && (
              <motion.div key="wip" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.35 }}>
                <SectionHeader
                  label="Coming Soon"
                  title="More work is on the way."
                  desc="These sections are currently being prepared. We'll be showcasing our work in UI/UX, branding, and content creation very soon."
                />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
                  {wipCategories.map((c, i) => <WIPCard key={i} title={c.title} desc={c.desc} index={i} />)}
                </div>

                <div style={{ marginTop: 48, padding: '32px 40px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span className="font-mono" style={{ fontSize: 10, color: 'var(--accent)', letterSpacing: '0.1em' }}>IN THE MEANTIME</span>
                  <p className="heading-md" style={{ fontSize: 20 }}>Have a project in mind?</p>
                  <p className="body-base" style={{ fontSize: 14, maxWidth: 480 }}>
                    Our work speaks for itself in conversations. Let's talk about what you need — we'll show you examples that are relevant to your brief.
                  </p>
                  <Link to="/contact" style={{ textDecoration: 'none', display: 'inline-flex', width: 'fit-content', marginTop: 8 }} data-testid="wip-contact-link">
                    <span className="btn-primary" style={{ fontSize: 12 }}>Get in Touch</span>
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--ink-950)', paddingTop: 80, paddingBottom: 80 }}>
        <div className="container-site" style={{ textAlign: 'center' }}>
          <AnimatedSection>
            <span className="label-sm" style={{ color: 'var(--ink-500)', justifyContent: 'center' }}>Start a Project</span>
            <h2 className="heading-lg" style={{ fontSize: 'clamp(28px, 5vw, 56px)', color: 'var(--canvas)', margin: '16px 0 32px' }}>
              Your project could be next.
            </h2>
            <Link to="/contact" data-testid="case-studies-cta-btn" style={{ textDecoration: 'none' }}>
              <motion.button
                style={{ background: 'var(--canvas)', color: 'var(--ink-950)', border: 'none', padding: '13px 28px', borderRadius: 6, fontFamily: 'Inter', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              >
                Book a Strategy Call <ArrowUpRight size={14} />
              </motion.button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
